import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Dimmer,
  Header,
  Icon,
  Label,
  Loader,
  Modal,
  Table,
} from 'semantic-ui-react';

import qs from 'qs';
import fileSize from '../../utils/fileSize';
import { flashErrorMessage, flashSuccessMessage } from '../../actions/flash';
import { promptChangesMessage } from '../../actions/prompt';
import { fetchM } from '../../actions/fetchM';
import { TRANSLATION_FILES } from '../../constants/translation';
import PageNav from '../../components/PageNav';
import UploadModal from '../../components/UploadModal';

import {
  UPLOAD_DOCUMENT_PROMPT,
} from '../../constants/promptMessages';

import Form from '../Documents/Form';

class AddDocument extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    onUpload: PropTypes.func,
    onCheck: PropTypes.func,
    where: PropTypes.shape({}),
    t: PropTypes.func.isRequired,
  }
  constructor(props) {
    super(props);
    this.state = {
      count: 0,
      limit: 0,
      offset: 0,
      loading: true,
      data: [],
      currentPage: 1,
    };
  }
  componentWillReceiveProps(nextProps) {
    // fetch only when open modal
    if (nextProps.open === true) {
      this.fetchDocuments();
    }
  }
  setDocuments(data = []) {
    return new Promise((res) => {
      this.setState({
        data: data || [],
      }, () => {
        res(data);
      });
    });
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  async fetchDocuments() {
    await this.setLoading(true);
    const { store } = this.context;
    const query = Object.assign(this.props.where || {}, {
      page: this.state.currentPage || 1,
    });
    let res;
    try {
      // TODO: remove arrayFormat after add query array check in golang
      res = await store.dispatch(fetchM(`/documents/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const count = parseInt(res.headers.get('M-Data-count'), 10);
      const limit = parseInt(res.headers.get('M-Data-limit'), 10);
      const offset = parseInt(res.headers.get('M-Data-offset'), 10);
      const data = await res.json();
      await this.setDocuments(data);
      if (this.state.data.length < 1 && query.page > 1) {
        this.handleChangePage(1);
      }
      // handle token.. etc
      await this.setComponentState({
        count,
        limit,
        offset,
      });
    }
    await this.setLoading(false);
  }
  handleChangePage = (currentPage) => {
    this.setState({ currentPage }, () => {
      this.fetchDocuments();
    });
  }
  handleAddDocument = (e, data) => {
    e.preventDefault();
    if (this.props.onConfirm) {
      this.props.onConfirm(e, data);
    }
  }
  handleOnClose = (e) => {
    if (this.props.onClose) {
      this.props.onClose(e);
    }
  }
  checkAdded(data) {
    if (this.props.onCheck) {
      return this.props.onCheck(data);
    }
    return false;
  }
  handleOnUpload = (e, data) => {
    if (this.props.onUpload) {
      e.preventDefault();
      const { url, options } = this.props.onUpload(data);
      if (url && options) {
        this.upload(url, options);
      }
    }
  }
  async upload(url, options) {
    const { store } = this.context;
    const { t } = this.props;
    try {
      await this.form.submit(url, options);
    } catch (err) {
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        let message = await err.res.text();
        if (err.res.status === 413) {
          message = t('Uploaded document/image too large', { ns: TRANSLATION_FILES.translation });
        }
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      store.dispatch(flashErrorMessage(err));
      return;
    }
    this.uploadModal.close();
    this.handleChangePage(this.state.currentPage);
    store.dispatch(flashSuccessMessage({ header: t('Upload Success', { ns: TRANSLATION_FILES.translation }), message: t('Document(s) successfully uploaded', { ns: TRANSLATION_FILES.translation }) }));
  }
  handleCheckBeforeClose = () => {
    if (this.form.state.uploads.length > 0) {
      const callback = () => {
        this.uploadModal.close();
        this.handleChangePage(this.state.currentPage);
      };
      const { store } = this.context;
      store.dispatch(promptChangesMessage({ message: UPLOAD_DOCUMENT_PROMPT, callback }));
      return true;
    }
    return false;
  }
  render() {
    const { t } = this.props;
    return (
      <Modal
        className="add-document"
        open={this.props.open === true}
        onClose={this.handleOnClose}
        closeIcon="close"
        size="large"
      >
        <Header icon="image outline" content={t('Add document', { ns: TRANSLATION_FILES.translation })} />
        <Modal.Content scrolling>
          <Dimmer style={{ zIndex: 20 }} active={this.state.loading} inverted>
            <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
          </Dimmer>
          <Table
            striped unstackable
            selectable
          >
            <Table.Header fullWidth>
              <Table.Row>
                <Table.HeaderCell colSpan={4}>
                  {
                    this.props.onUpload ? (
                      <UploadModal
                        floated="right"
                        ref={(ref) => { this.uploadModal = ref; }}
                        onConfirm={this.handleOnUpload}
                        onClose={this.handleCheckBeforeClose}
                        content={({ onConfirm }) => (
                          <Form
                            ref={(ref) => { this.form = ref; }}
                            onSubmit={onConfirm}
                          />
                        )}
                      />
                    ) : null
                  }
                  <PageNav
                    floated="right"
                    currentPage={this.state.currentPage}
                    count={this.state.count}
                    limit={this.state.limit}
                    offset={this.state.offset}
                    dataSize={this.state.data.length}
                    loading={this.state.loading}
                    onChange={this.handleChangePage}
                  />
                </Table.HeaderCell>
              </Table.Row>
              <Table.Row>
                <Table.HeaderCell>{t('Name', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                <Table.HeaderCell>{t('Size', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                <Table.HeaderCell>{t('Tags', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {
                this.state.data.length > 0 ?
                  this.state.data.map((file, key) => (
                    <Table.Row key={key}>
                      <Table.Cell>
                        <Icon name="file pdf outline" />
                        {file.name}
                      </Table.Cell>
                      <Table.Cell collapsing>
                        {fileSize(file.size)}
                      </Table.Cell>
                      <Table.Cell>
                        <Label.Group size="tiny">
                          {
                            file.tags && file.tags.length > 0 ?
                            file.tags.map((tag, idx) => (
                              <Label key={idx} as="a">{tag}</Label>
                            ))
                            : <p>{t('No tags set', { ns: TRANSLATION_FILES.translation })}</p>
                          }
                        </Label.Group>
                      </Table.Cell>
                      <Table.Cell collapsing>
                        <Button
                          size="tiny"
                          fluid
                          compact
                          primary={!this.checkAdded(file)}
                          positive={this.checkAdded(file)}
                          icon={this.checkAdded(file) ? 'checkmark' : 'add'}
                          content={this.checkAdded(file) ? t('Added', { ns: TRANSLATION_FILES.translation }) : t('Add', { ns: TRANSLATION_FILES.screens })}
                          disabled={this.checkAdded(file)}
                          onClick={e => this.handleAddDocument(e, file)}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))
                :
                  <Table.Row>
                    <Table.Cell colSpan={4} textAlign="center">
                      {t('No documents found.', { ns: TRANSLATION_FILES.translation })}
                    </Table.Cell>
                  </Table.Row>
              }
            </Table.Body>
            <Table.Footer fullWidth>
              <Table.Row>
                <Table.HeaderCell colSpan={4} textAlign="right">
                  <PageNav
                    currentPage={this.state.currentPage}
                    count={this.state.count}
                    limit={this.state.limit}
                    offset={this.state.offset}
                    dataSize={this.state.data.length}
                    loading={this.state.loading}
                    onChange={this.handleChangePage}
                  />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </Modal.Content>
      </Modal>
    );
  }
}

export default withTranslation()(AddDocument);



// WEBPACK FOOTER //
// ./src/containers/modals/AddDocument.jsx